import { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { Button, Dialog } from '@netfront/ui-library';

import { Card, Container, InviteTeacherDialog, LessonCards, Page } from '@/components';
import { useGetLessons, useGetUserFlowStep } from '@/hooks';
import { getCompletedLessonsCount } from '@/utils';
import { CONSTANTS } from '@/constants';
import { useRouter } from 'next/router';

export const TeacherDashboard = () => {
  const { push } = useRouter();
  const { lessons, isLoading: isLessonsLoading = false } = useGetLessons();
  const [hasCompletedSurvey, setHasCompletedSurvey] = useState<boolean>(false);
  const [isSurveyDialogOpen, setIsSurveyDialogOpen] = useState<boolean>(false);
  const [isSurveyStepLoaded, setIsSurveyStepLoaded] = useState(false);
  const [isInviteTeacherDialogOpen, setIsInviteTeacherDialogOpen] = useState<boolean>(false);


  const handleRedirectToSurvey = () => {
    setIsSurveyDialogOpen(false);
    push(`/surveys/${CONSTANTS.USERFLOW.SURVEY.TEACHER.URL}/${CONSTANTS.USERFLOW.SURVEY.TEACHER.MODULE.URL}`);
  };

  const { handleGetUserFlowStep } = useGetUserFlowStep({
    onCompleted: ({ userFlowStep: returnedUserFlowStep }) => {
      const { isComplete = false } = returnedUserFlowStep ?? {};
      setHasCompletedSurvey(isComplete);
      setIsSurveyStepLoaded(true);
    },
    query: gql`
      query getUserFlowStep($userFlowStepId: Int!) {
        userFlowStep {
          getUserFlowStep(userFlowStepId: $userFlowStepId) {
            isComplete
          }
        }
      }
    `
  });

  const lessonsExludingBackstory = lessons?.filter(({ id }) => id !== CONSTANTS.USERFLOW.CARTOONS.STEPS.BACKSTORY.ID);

  const completedLessonsCount = useMemo(() => getCompletedLessonsCount(lessonsExludingBackstory), [lessonsExludingBackstory]);

  const hasCompletedAllLessons = useMemo(() => {
    return lessonsExludingBackstory.length > 0 ? completedLessonsCount === lessonsExludingBackstory.length : false;
  }, [completedLessonsCount, lessonsExludingBackstory]);
  
  useEffect(() => {
    if (!hasCompletedAllLessons || hasCompletedSurvey || !isSurveyStepLoaded) {
      return;
    }
    setIsSurveyDialogOpen(true);
  }, [hasCompletedAllLessons, hasCompletedSurvey, isSurveyStepLoaded]);

  useEffect(() => {

    handleGetUserFlowStep({
      userFlowStepId: CONSTANTS.USERFLOW.SURVEY.TEACHER.ID
    });
  }, []);

  return (
    <Page
      hasPrivateLayout
      meta={{
        seoDescription: 'View and complete your lessons',
        seoTitle: 'Teacher lessons',
      }}
      title="Teacher lessons"
    >
      <div className="bg-primary bg-banner-graphic py-5">
        <Container>
          <div className="flex flex-col md:flex-row md:items-center">
            <div className="flex items-center flex-col md:flex-row flex-1 gap-4">
              <img src="/images/lesson-icon.svg" alt="Cartoon of a slide projector with abstract content." className='max-w-16' />
              <div className='flex-1'>
                <h1 className="h3 text-uppercase">Teacher Dashboard</h1>
              </div>

              <Button
                text='Invite another teacher'
                variant='dark'
                onClick={() => setIsInviteTeacherDialogOpen(true)}
              />
            </div>
          </div>
        </Container>
      </div>

      <section className="bg-white py-6">
        <Container>
          <Card additionalClassNames='!py-6'>
            <div className="flex flex-col lg:flex-row items-start mb-4">
              <div className="flex-1">
                <div className="flex flex-col items-start md:flex-row md:items-center w-full">
                  <img className="max-w-10 mr-4 mb-4 lg:mb-0" src="/images/hat-icon.svg" alt="Info" />
                  <p>Access teacher resources, students progress and manage accounts</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row">
              <Card additionalClassNames="!p-4 flex flex-col flex-1 items-start md:flex-row border-grey-500 shadow-none w-full md:w-1/2 mb-4 md:mb-0 md:mr-4">
                <img className="max-w-20 mx-auto mb-4 md:mb-0 md:mr-4" src="/images/person-icon.svg" alt="Lesson" />
                <div className='flex flex-col md:flex-row items-center w-full gap-2'>
                  <h2 className="h4 mb-3 flex-1">
                    <span className="h6">Manage</span>
                    <br />
                    Students
                  </h2>
                  <Button 
                    linkButton={{ id: 'student-management', url: '/teacher/student-management' }} 
                    variant="tertiary" 
                    text="Access" 
                    size='small'
                  />
                </div>
              </Card>
              <Card additionalClassNames="!p-4 flex flex-col flex-1 items-start md:flex-row border-grey-500 shadow-none w-full md:w-1/2">
                <img className="mx-auto max-w-20 mb-4 md:mb-0 md:mr-4" src="/images/lesson-icon.svg" alt="Guidelines" />
                <div className='flex flex-col md:flex-row items-center w-full gap-2'>
                  <h2 className="h4 mb-3 flex-1">
                    <span className="h6">Teacher</span>
                    <br />
                    Resources
                  </h2>
                  <Button
                    linkButton={{ id: 'teacher-resources', url: '/teacher/resources' }}
                    variant="tertiary"
                    text="Access"
                    size='small'
                  />
                </div>
              </Card>
            </div>
          </Card>
        </Container>
      </section>

      <section className="bg-grey-300 py-6">
        <Container>
          <h2 className="h5 flex items-center mb-4">
            <span className="text-xl flex items-center justify-center w-20 h-20 bg-green-500 rounded-circle border-5 border-white color-white mr-2">
              {completedLessonsCount}
              {` `}
              <span className="text-sm weight-500 block mx-1">of</span>
              {` `}
              {lessonsExludingBackstory.length}
            </span>
            <span className="text-uppercase">lessons complete</span>
          </h2>

          <Card additionalClassNames="!p-4 mb-8">
            <h3 className="h6 mb-4 text-uppercase">Useful links</h3>
            <div className="flex flex-wrap">
              <Button 
                additionalClassNames="mb-3 md:mb-0 mr-2" 
                text="Characters (A3 Poster)" 
                variant="dark" 
                linkButton={{
                  id: 'charcter-poster',
                  url: 'https://resources.ekardo.com/production/project/parenting-and-teens/Document/public/posterh4lcharacters-a3-f02d174a3cd64d3fa6f056d2bfdc57e2.pdf',
                  target: '_blank'
                }} 
              />
              <Button 
                additionalClassNames="mb-3 md:mb-0 mr-2" 
                text="Standard drink chart" 
                variant="dark" 
                linkButton={{
                  id: 'standard-drink-chart',
                  url: 'https://resources.ekardo.com/production/project/parenting-and-teens/Document/public/module2studentsummary-5-1-46329ddd12d342a8906b681eacc55f84.pdf',
                  target: '_blank'
                }}
              />
              <Button 
                additionalClassNames="mb-3 md:mb-0 mr-2" 
                text="Dietary Guidelines" 
                variant="dark" 
                linkButton={{
                  id: 'dietary-guidelines',
                  url: 'https://resources.ekardo.com/production/project/parenting-and-teens/Document/public/n55f_children_brochure-1-91e487f84cb24c0aaa3bcba82faf90ed.pdf',
                  target: '_blank'
                }}
              />
            </div>
          </Card>

          <LessonCards lessons={lessons} isLoading={isLessonsLoading} roleTag="Teacher" />
        </Container>
      </section>
      {isSurveyDialogOpen && (
        <Dialog
          isOpen
          title="Congratulations"
          onClose={() => setIsSurveyDialogOpen(false)}
          onConfirm={handleRedirectToSurvey}
          confirmText="Continue to Survey"
        >
          <p>
            You have completed all the lessons! Please continue to the survey to provide feedback on the lessons and your experience.
          </p>
        </Dialog>
      )}

      <InviteTeacherDialog 
        isOpen={isInviteTeacherDialogOpen}
        onClose={() => setIsInviteTeacherDialogOpen(false)}
      />

    </Page>
  );
};
